/**
 * The customers module's URL adapter (docs/audits/ROLLOUT-CUSTOMERS-SHELL.md §2.1), the twin of
 * the pilot's useQuoteRoute: the same writer names and the same push/replace rules. Since O0 it
 * is built on the shared factory (layout/useRecordRoute.js, ROLLOUT-ORDERS-SHELL.md §3 D12);
 * what is Customers' own is below: its keys, its defaults, its aliases.
 *
 *   /customers              the list (list state in the query, defaults omitted)
 *   /customers/:id          that customer's record: the 360 pane beside the list (C1b); the query
 *                           rides along, so the list beside it keeps its view, q and page
 *   /customers/:id/edit     the edit form, in the record column beside the list (C1b, D5)
 *   /customers/new          the add form, likewise
 *   /customers/review-queue its own route (App.js), not this hook's
 *
 * History (pilot decision 3): list → record pushes; record → record replaces, so Back always
 * returns to the list in one step. Search, sort, dir and scope replace; page pushes, so Back
 * steps through pages.
 *
 * The list defaults (C4b) are the user's: customers.list.defaultView / sort / dir in their
 * preferences document (§5.1), over the built-in ones. They apply only where the URL omits the
 * key, and stay out of the URL (a bare /customers is the user's own default view; any other view,
 * All included when it is not the default, is explicit). Scope is not a preference (D2).
 */
import { useCallback, useMemo } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { isCustomerManager } from '../../utils/customerRoles';
import { createRecordRoute, positiveInt } from '../layout/useRecordRoute';

export const CUSTOMERS_ROOT = '/customers';

/** §3 D1. Every list request carries one (C1b); `unassigned` is the strict manager roles'. */
export const CUSTOMER_VIEWS = ['all', 'recent', 'open_quotes', 'at_risk', 'unassigned'];

// The sortable headers. `email` is not in §2.1's list but has a header today; kept, not dropped.
const SORTS = ['name', 'email', 'company', 'city', 'created'];
const DIRS = ['asc', 'desc'];
// `queue` is not a list scope: it is the review-queue page (a legacy alias, below).
const SCOPES = ['my', 'all'];

/** Keys that describe the list. They ride onto record and form URLs; any other key is dropped. */
const LIST_KEYS = ['view', 'q', 'page', 'sort', 'dir', 'scope'];

/** The built-in list defaults; a user's own come from customers.list (C4b, §5.1). */
export const LIST_DEFAULTS = Object.freeze({
  view: 'all', q: '', page: 1, sort: 'name', dir: 'asc', scope: 'my'
});

/**
 * The list defaults for a customers document and its user: list.defaultView, sort and dir over
 * the built-in ones, key by key. `unassigned` is the strict manager roles' view (the server 403s
 * it for anyone else), so for another user that one key falls back to `all`.
 */
export function listDefaultsFrom(doc, user) {
  const list = (doc && doc.list) || {};
  const d = { ...LIST_DEFAULTS };
  if (CUSTOMER_VIEWS.includes(list.defaultView)
    && (list.defaultView !== 'unassigned' || isCustomerManager(user))) d.view = list.defaultView;
  if (SORTS.includes(list.sort)) d.sort = list.sort;
  if (DIRS.includes(list.dir)) d.dir = list.dir;
  return Object.freeze(d);
}

/** This user's list defaults (C4b). */
export function useCustomerListDefaults(user) {
  const { doc } = usePreferences('customers');
  return useMemo(() => listDefaultsFrom(doc, user), [doc, user]);
}

/** Keys whose change pushes; the rest replace. */
const PUSH_KEYS = ['view', 'page'];
/** Keys that change which rows the list holds or their order: a change resets `page`. */
const FILTER_KEYS = ['view', 'q', 'sort', 'dir', 'scope'];

/** Dashboard filters (the QuotationManager translator) and the view each one means. */
const FILTER_VIEWS = { 'at-risk': 'at_risk' };

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

const isListPath = (pathname) => String(pathname || '').replace(/\/+$/, '') === CUSTOMERS_ROOT;

/**
 * Legacy inbound forms, normalised once (§2.1). Returns the canonical location, or null when
 * the URL is already canonical. Only the list URL carried them.
 */
export function legacyCustomerRedirect(pathname, search) {
  if (!isListPath(pathname)) return null;
  const sp = new URLSearchParams(search || '');
  let target = CUSTOMERS_ROOT;
  let changed = false;

  if (sp.get('scope') === 'queue') {
    return { pathname: `${CUSTOMERS_ROOT}/review-queue`, search: '' };
  }
  if (sp.has('selected')) {
    const selected = positiveInt(sp.get('selected'));
    sp.delete('selected');
    if (selected) target = `${CUSTOMERS_ROOT}/${selected}`;
    changed = true;
  }
  if (sp.has('filter')) {
    const view = FILTER_VIEWS[sp.get('filter')];
    sp.delete('filter');
    if (view) sp.set('view', view);
    changed = true;
  }

  if (!changed) return null;
  const qs = sp.toString();
  return { pathname: target, search: qs ? `?${qs}` : '' };
}

const customerRoute = createRecordRoute({
  root: CUSTOMERS_ROOT,
  listKeys: LIST_KEYS,
  defaults: LIST_DEFAULTS,
  useDefaults: useCustomerListDefaults,
  pushKeys: PUSH_KEYS,
  filterKeys: FILTER_KEYS,
  subRoutes: { edit: 'edit' },
  newMode: 'new',
  legacyRedirect: legacyCustomerRedirect,
  parseList: (sp, d) => ({
    view: oneOf(sp.get('view'), CUSTOMER_VIEWS, d.view),
    q: sp.get('q') || '',
    page: positiveInt(sp.get('page')) || 1,
    sort: oneOf(sp.get('sort'), SORTS, d.sort),
    dir: oneOf(sp.get('dir'), DIRS, d.dir),
    scope: oneOf(sp.get('scope'), SCOPES, d.scope)
  })
});

/**
 * Pure parse of a customers location.
 * @param {string} pathname - e.g. '/customers/12/edit'
 * @param {string} search - e.g. '?q=smith&page=2'
 * @param {object} [defaults] - the list defaults (listDefaultsFrom); the built-in ones if omitted
 */
export function parseCustomerLocation(pathname, search, defaults = LIST_DEFAULTS) {
  return customerRoute.parse(pathname, search, defaults);
}

/** Keys a view link carries over from the list on screen: how it is searched, sorted, scoped. */
const VIEW_KEEPS = ['q', 'sort', 'dir', 'scope'];

/**
 * The list URL for a view (C3, the views menu). On the customers list it keeps q, sort, dir
 * and scope (so the count beside the view is what the view shows) and starts on page 1;
 * anywhere else it is the bare view. The user's default view leaves the URL (C4b: `all` when
 * nothing is stored), so every other view, All included, is explicit.
 */
export function customerViewHref(view, { pathname = '', search = '' } = {}, defaults = LIST_DEFAULTS) {
  const sp = new URLSearchParams();
  if (String(pathname || '').startsWith(CUSTOMERS_ROOT)) {
    const current = new URLSearchParams(search || '');
    for (const key of VIEW_KEEPS) {
      const value = current.get(key);
      if (value) sp.set(key, value);
    }
  }
  if (view && view !== (defaults || LIST_DEFAULTS).view && CUSTOMER_VIEWS.includes(view)) sp.set('view', view);
  const qs = sp.toString();
  return `${CUSTOMERS_ROOT}${qs ? `?${qs}` : ''}`;
}

/** The list URL a Dashboard `{ filter }` payload means, canonical (no alias hop). */
export function customerListHref({ filter } = {}) {
  const view = FILTER_VIEWS[filter];
  return view ? `${CUSTOMERS_ROOT}?view=${view}` : CUSTOMERS_ROOT;
}

/**
 * @param {object} [user] the signed-in user: whose list defaults apply (an `unassigned` default
 *   needs a manager role). Without one the built-in defaults hold for that key.
 * @returns the parse ({ mode, id, invalidId, problem, list }), `defaults`, and the writers
 *   openRecord, closeRecord, editRecord, newCustomer, setList.
 */
export default function useCustomerRoute(user) {
  const route = customerRoute.useRecordRoute(user);
  const { openSubRoute } = route;
  const editRecord = useCallback((id) => openSubRoute(id, 'edit'), [openSubRoute]);
  // Same identity rules as before the factory: a new object only when the route changes.
  return useMemo(() => {
    const { openSubRoute: _sub, openNew, ...rest } = route;
    return { ...rest, editRecord, newCustomer: openNew };
  }, [route, editRecord]);
}
